// src/components/WeekViewWithStore.tsx
import React, { useState, useMemo } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import { Calendar, Views, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { Gathering, GatheringStatus } from '../../../../types';
import { useGatheringsForDateRange } from '../../hooks/data/useGatheringsForDateRange';
import { getWeekRange } from '../../../../utils/dateRangeUtils';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { ChevronLeft, ChevronRight, Today } from '@mui/icons-material';
import { FilterState } from '../../hooks/useFilters';

// Setup the localizer
const localizer = momentLocalizer(moment);

interface WeekViewProps {
  token: string | null;
  onViewDetails: (gatheringId: string) => void;
  filters: FilterState;
}

const WeekView: React.FC<WeekViewProps> = ({ 
  token,
  onViewDetails,
  filters
}) => {
  // Manage current date internally
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  
  // Calculate week date range (starting on Monday) - memoize to prevent recalculation on every render
  const dateRange = useMemo(() => {
    return getWeekRange(currentDate, 1); // 1 = Monday
  }, [currentDate]);
  
  const [startDate, endDate] = dateRange;
  
  // Use the core date range hook directly
  const { 
    gatherings: allWeekGatherings, 
    isLoading, 
    isBackgroundLoading,
    error, 
    refresh 
  } = useGatheringsForDateRange(startDate, endDate, token);
  
  // Apply filters to gatherings - memoize to prevent recalculation on every render
  const weekGatherings = useMemo(() => {
    return allWeekGatherings.filter(gathering => {
      // Apply space filter
      if (filters.spaceId && gathering.space?.id !== filters.spaceId) {
        return false;
      }

      // Date range filter is not needed here since we're already filtering by week
      // and the date range filter would be redundant with the week view's built-in date range

      // Gathering passed all filters
      return true;
    });
  }, [allWeekGatherings, filters.spaceId]);
  
  // Navigation handlers
  const handlePrevWeek = () => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setDate(newDate.getDate() - 7);
      return newDate;
    });
  };
  
  const handleNextWeek = () => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setDate(newDate.getDate() + 7);
      return newDate;
    });
  };
  
  const handleToday = () => {
    setCurrentDate(new Date());
  };
  
  // Convert gatherings to events for the calendar
  const events = useMemo(() => {
    return weekGatherings.map(gathering => {
      // Extract date part from the ISO string
      const dateStr = gathering.date;
      
      // Parse start and end times correctly
      let startTime = gathering.start_time;
      let endTime = gathering.end_time;
      
      // Create date objects for start and end
      const startDate = moment(`${dateStr} ${startTime}`, 'YYYY-MM-DD hh:mm a').toDate();
      const endDate = moment(`${dateStr} ${endTime}`, 'YYYY-MM-DD hh:mm a').toDate();
      
      return {
        id: gathering.id,
        title: gathering.title,
        start: startDate,
        end: endDate,
        status: gathering.status,
        allDay: false
      };
    });
  }, [weekGatherings]);
  
  // Style events based on status
  const eventStyleGetter = (event: any) => {
    let backgroundColor = '#3174ad'; // default
    
    switch(event.status) {
      case GatheringStatus.NEW:
        backgroundColor = '#42a5f5'; // light blue
        break;
      case GatheringStatus.IN_REVIEW:
        backgroundColor = '#ffb74d'; // orange
        break;
      case GatheringStatus.NEED_MORE_INFO:
        backgroundColor = '#ef5350'; // red
        break;
      case GatheringStatus.RESCHEDULE:
        backgroundColor = '#9575cd'; // purple
        break;
      case GatheringStatus.AWAITING_GATHERING_LINK:
        backgroundColor = '#4db6ac'; // teal
        break;
      case GatheringStatus.READY_TO_PUBLISH:
        backgroundColor = '#66bb6a'; // green
        break;
      case GatheringStatus.PUBLISHED:
        backgroundColor = '#26a69a'; // darker green
        break;
      case GatheringStatus.DENIED:
        backgroundColor = '#e57373'; // light red
        break;
    }
    
    const style = {
      backgroundColor,
      color: '#fff',
      borderRadius: '4px',
      border: 'none',
      display: 'block'
    };
    return {
      style
    };
  };
  
  return (
    <Box className="week-view">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">
          Week of {moment(startDate).format('MMMM D, YYYY')}
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={handlePrevWeek} size="small">
            <ChevronLeft />
          </IconButton>
          <IconButton onClick={handleToday} size="small">
            <Today />
          </IconButton>
          <IconButton onClick={handleNextWeek} size="small">
            <ChevronRight />
          </IconButton>
        </Box>
      </Box>
      
      {/* Background loading indicator */}
      {isBackgroundLoading && (
        <Box sx={{ 
          position: 'absolute', 
          top: '60px', 
          right: '20px',
          zIndex: 1000,
          backgroundColor: 'rgba(255,255,255,0.8)',
          padding: '5px 10px',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div className="spinner-small"></div>
          <Typography variant="caption">Refreshing...</Typography>
        </Box>
      )}
      
      {/* Error message */}
      {error && (
        <Box sx={{ color: 'error.main', mb: 2, textAlign: 'center' }}>
          {error}
          <button onClick={refresh} className="small-button">Retry</button>
        </Box>
      )}
      
      {/* Loading state */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <div className="spinner"></div>
        </Box>
      ) : (
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 700 }}
          defaultView={Views.WEEK}
          date={currentDate}
          onNavigate={date => setCurrentDate(date)}
          eventPropGetter={eventStyleGetter}
          onSelectEvent={event => onViewDetails(event.id)}
          views={['week', 'day']}
          min={moment().hours(6).minutes(0).seconds(0).toDate()} // Start at 6:00 AM
          max={moment().hours(21).minutes(0).seconds(0).toDate()} // End at 9:00 PM
        />
      )}
    </Box>
  );
};

export default WeekView;